import React from "react";
/*
 * ui.tsx — dep-free primitives for the Import Hub module.
 *
 * Drop-in replacements for the shadcn/Radix components the module used
 * (Button, Input, Textarea, Label, Select, Dialog, Sheet, Tooltip). All
 * styling comes from importHub.css semantic classes.
 */
import {
  forwardRef,
  useEffect,
  useRef,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import { Lock, X } from "./icons";

/** Tiny class-name joiner. Replaces `cn`/`clsx`/`tailwind-merge`. */
export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/* ============================== Button ============================== */
type BtnVariant = "primary" | "outline" | "ghost" | "danger" | "link";
type BtnSize = "sm" | "md" | "lg" | "icon";
export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: BtnVariant;
  size?: BtnSize;
  /** Renders disabled with a lock icon and a "requires Business license" title. */
  locked?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "outline", size = "md", className, type = "button", locked, disabled, title, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || locked}
      title={locked ? "Requires a Business license" : title}
      className={cx(
        "ih-btn",
        variant === "primary" && "ih-btn-primary",
        variant === "outline" && "ih-btn-outline",
        variant === "ghost" && "ih-btn-ghost",
        variant === "danger" && "ih-btn-danger",
        variant === "link" && "ih-btn-link",
        size === "sm" && "ih-btn-sm",
        size === "lg" && "ih-btn-lg",
        size === "icon" && "ih-btn-icon",
        className,
      )}
      {...rest}
    >
      {locked ? (
        size === "icon" ? (
          <Lock size={16} />
        ) : (
          <>
            <Lock size={14} />
            {children}
          </>
        )
      ) : (
        children
      )}
    </button>
  );
});

/* ============================== Input / Textarea / Label ============================== */
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={cx("ih-input", className)} {...rest} />;
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return <textarea ref={ref} className={cx("ih-textarea", className)} {...rest} />;
  },
);

export const Label = forwardRef<HTMLLabelElement, LabelHTMLAttributes<HTMLLabelElement>>(
  function Label({ className, ...rest }, ref) {
    return <label ref={ref} className={cx("ih-label", className)} {...rest} />;
  },
);

/* ============================== Native <select>-based Select ==============================
 * A visual-only replacement for shadcn's Select. Consumers still pass
 * `value`/`onValueChange` to match the old API surface, but under the
 * hood we render a plain native <select>. That means no Radix, no
 * portal, no keyboard-model reimplementation — and it still supports
 * form controls out of the box.
 */
export type SelectOption = { value: string; label: string; disabled?: boolean };

export const Select: React.FC<Omit<SelectHTMLAttributes<HTMLSelectElement>, "onChange"> & {
  value?: string;
  onValueChange?: (v: string) => void;
  children?: ReactNode;
}> = ({
  value,
  onValueChange,
  children,
  className,
  ...rest
}) => {
  return (
    <select
      className={cx("ih-select", className)}
      value={value}
      onChange={(e) => onValueChange?.(e.target.value)}
      {...rest}
    >
      {children}
    </select>
  );
}
// Shim the old shadcn subcomponent surface so call sites keep compiling.
export const SelectTrigger: React.FC<{ children?: ReactNode; className?: string }> = ({ children }) => {
  return <>{children}</>;
}
export const SelectValue: React.FC<{ placeholder?: string }> = (_props) => {
  return null;
}
export const SelectContent: React.FC<{ children?: ReactNode }> = ({ children }) => {
  return <>{children}</>;
}
export const SelectItem: React.FC<{
  value: string;
  children?: ReactNode;
  disabled?: boolean;
}> = ({
  value,
  children,
  disabled,
}) => {
  return (
    <option value={value} disabled={disabled}>
      {typeof children === "string" ? children : (value as ReactNode)}
    </option>
  );
}

/* ============================== Dialog / Modal ============================== */
function useEscape(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
}

function useBodyLock(open: boolean) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);
}

export const Dialog: React.FC<{
  open: boolean;
  onOpenChange: (v: boolean) => void;
  children: ReactNode;
}> = ({
  open,
  onOpenChange,
  children,
}) => {
  const close = () => onOpenChange(false);
  useEscape(open, close);
  useBodyLock(open);
  if (!open) return null;
  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      className="ih-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
      role="presentation"
    >
      {children}
    </div>,
    document.body,
  );
}

export const DialogContent: React.FC<{
  className?: string;
  children: ReactNode;
  onClose?: () => void;
  size?: "md" | "lg" | "xl";
}> = ({
  className,
  children,
  onClose,
  size = "md",
}) => {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className={cx(
        "ih-dialog",
        size === "lg" && "ih-dialog-lg",
        size === "xl" && "ih-dialog-xl",
        className,
      )}
    >
      {onClose && (
        <button
          type="button"
          aria-label="Close"
          className="ih-dialog-close"
          style={{ position: "absolute", right: 12, top: 12 }}
          onClick={onClose}
        >
          <X size={16} />
        </button>
      )}
      {children}
    </div>
  );
}

export const DialogHeader: React.FC<{
  children: ReactNode;
  className?: string;
}> = ({
  children,
  className,
}) => {
  return <div className={cx("ih-dialog-header", className)}>{children}</div>;
}

export const DialogTitle: React.FC<{
  children: ReactNode;
  className?: string;
}> = ({
  children,
  className,
}) => {
  return <h2 className={cx("ih-dialog-title", className)}>{children}</h2>;
}

export const DialogFooter: React.FC<{
  children: ReactNode;
  className?: string;
}> = ({
  children,
  className,
}) => {
  return <div className={cx("ih-dialog-footer", className)}>{children}</div>;
}

export const DialogBody: React.FC<{
  children: ReactNode;
  className?: string;
}> = ({
  children,
  className,
}) => {
  return <div className={cx("ih-dialog-body", className)}>{children}</div>;
}

/* ============================== Sheet / Drawer (side panel) ============================== */
export const Sheet: React.FC<{
  open: boolean;
  onOpenChange: (v: boolean) => void;
  children: ReactNode;
}> = ({
  open,
  onOpenChange,
  children,
}) => {
  const close = () => onOpenChange(false);
  useEscape(open, close);
  useBodyLock(open);
  if (!open) return null;
  if (typeof document === "undefined") return null;
  return createPortal(
    <>
      <div className="ih-modal-backdrop" style={{ padding: 0 }} onMouseDown={close} role="presentation" />
      <div role="dialog" aria-modal="true" className="ih-drawer">
        {children}
      </div>
    </>,
    document.body,
  );
}

export const SheetHeader: React.FC<{ children: ReactNode; className?: string }> = ({ children, className }) => {
  return <div className={cx("ih-drawer-header", className)}>{children}</div>;
}
export const SheetTitle: React.FC<{ children: ReactNode; className?: string }> = ({ children, className }) => {
  return <h2 className={cx("ih-dialog-title", className)}>{children}</h2>;
}
export const SheetBody: React.FC<{ children: ReactNode; className?: string }> = ({ children, className }) => {
  return <div className={cx("ih-drawer-body", className)}>{children}</div>;
}
export const SheetFooter: React.FC<{ children: ReactNode; className?: string }> = ({ children, className }) => {
  return <div className={cx("ih-drawer-footer", className)}>{children}</div>;
}

/* ============================== Tooltip (CSS-only) ============================== */
export const Tooltip: React.FC<{
  content: ReactNode;
  children: ReactNode;
  className?: string;
}> = ({
  content,
  children,
  className,
}) => {
  return (
    <span className={cx("ih-tooltip", className)} tabIndex={-1}>
      {children}
      <span className="ih-tooltip-content" role="tooltip">
        {content}
      </span>
    </span>
  );
}
// Shims for the old shadcn Tooltip.* API surface
export const TooltipProvider: React.FC<{ children: ReactNode; delayDuration?: number }> = ({ children }) => {
  return <>{children}</>;
}
export const TooltipTrigger: React.FC<{ children: ReactNode; asChild?: boolean }> = ({ children }) => {
  return <>{children}</>;
}
export const TooltipContent: React.FC<{ children: ReactNode; className?: string }> = ({ children }) => {
  return <span className="ih-tooltip-content">{children}</span>;
}

/* ============================== Utility hook ============================== */
/** Track whether a ref is inside the DOM (useful for focus traps). */
export function useMounted() {
  const ref = useRef(false);
  useEffect(() => {
    ref.current = true;
    return () => {
      ref.current = false;
    };
  }, []);
  return ref;
}

/** Generic props alias for extended `className` on wrapper spans. */
export type SpanProps = HTMLAttributes<HTMLSpanElement>;
